import { AddBreed, CreateSpecies, UpdateSpeciesIsIndoorOutdoorPet, UpdateSpeciesUsesLitterBox } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/PathItems";
import { GetSpecies } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/PathItems";
import { SearchSpecies } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/PathItems";
import { DeleteSpecies } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/PathItems";
import { UpdateSpeciesName } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/PathItems";
import { UpdateSpeciesDescription } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/PathItems";
import { UpdateSpeciesThumbnail } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/PathItems";
import { UpdateSpeciesUtilizesCommands } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/PathItems";
import { UpdateSpeciesState } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/PathItems";

import { MoveBreed, RemoveBreed } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/Breeds/PathItems";
import { UpdateBreedName } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/Operations/Server/Breeds/PathItems";
import { Breed, Species } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Species/PathItems";
import { SearchSpeciesOutput } from "../../PetKey/Main/Model/1_0_Initial/PathItems";
import { generateUUID, Result, TransportAbstractionBuilder, TransportContext } from "@peercolab/engine";
import { applyStateTransition, handleInPlaceMoveSortOfIdElement, transitionState } from "../../../utils/GeneralHelpers";
import { TranslationAppender } from "./translationMocks";

export function mockSpeciesInterceptors(builder: TransportAbstractionBuilder, appender: TranslationAppender) {
  const dbSpecies: Species[] = [] 
  const dbSpeciesPublished: Species[] = [] 

  type BreedAndSpecies = {
    species: Species,
    breed: Breed,
  }

  const getSpecies = (ctx: string | TransportContext): Result<Species> => {
    const id = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('speciesId');
    const species = dbSpecies.find(s => s.id === id);
    if (!species) {
      return Result.notFound('Backoffice.Species.NotFound', `Species with id ${id} not found`);
    }
    return Result.ok(species);
  }

  const getBreed = (ctx: string | TransportContext, breedId?: string): Result<BreedAndSpecies> => {
    const speciesId = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('speciesId');
    const extractedBreedId = typeof ctx === 'string' ? breedId : ctx.getPathParameter<string>('breedId');
    const species = dbSpecies.find(s => s.id === speciesId);
    if (!species) {
      return Result.notFound('Backoffice.Species.NotFound', `Species with id ${speciesId} not found`);
    }
    const breed = species.breeds.map(b => b.breed).find(b => b.id === extractedBreedId);
    if (!breed) {
      return Result.notFound('Backoffice.Species.Breeds.NotFound', `Breed with id ${extractedBreedId} not found in species ${speciesId}`);
    }
    return Result.ok({ species, breed });
  }

  const saveSpecies = (result: Result<Species>, ctx: TransportContext): Result<Species> => {
    if (!result.success) {
      return result;
    }
    const species = result.value;
    const existingIndex = dbSpecies.findIndex(s => s.id === species.id);
    if (existingIndex !== -1) {
      const existingPublishedIndex = dbSpeciesPublished.findIndex(s => s.id === species.id);
      dbSpecies[existingIndex] = applyStateTransition(dbSpeciesPublished, existingPublishedIndex, result.meta, species, species.state, (item, newState) => {
        item.state = newState;
        return item;
      });
    } else {
      species.translationKey = appender('species', species.name);
      dbSpecies.push(species);
    }
    return Result.ok(species);
  }

  return builder
    .intercept(new SearchSpecies().handle(async (input, ctx) => {
      const filtered = dbSpecies
        .slice(input.offset, input.offset + input.limit)
        .filter(s => 
          s.name.toLowerCase().includes(input.query.toLowerCase()) ||
          s.description.toLowerCase().includes(input.query.toLowerCase())
        )
        .map(s => {
          const item: SearchSpeciesOutput = {
            id: s.id,
            name: s.name,
            numberOfBreeds: s.breeds.length,
            publishedBreeds: s.breeds.length,
            state: s.state,
          }
          return item;
        });
      return Result.ok(filtered);
    }))
    .intercept(new GetSpecies().handle(async (input, ctx) => {
      return getSpecies(ctx)
    }))
    .intercept(new CreateSpecies().handle(async (input, ctx) => {
      const newSpecies: Species = {
        id: input.speciesId ?? generateUUID(),
        name: input.name,
        description: input.description ?? '',
        translationKey: '',
        utilizesCommands: false,
        usesLitterBox: false,
        isIndoorOutdoorPet: false,
        breeds: [],
        state: { state: 'draft', isDirty: false }
      }
      return saveSpecies(Result.ok(newSpecies), ctx);
    }))
    .intercept(new DeleteSpecies().handle(async (input, ctx) => {
      const id = ctx.getPathParameter<string>('speciesId');
      const idx = dbSpecies.findIndex(s => s.id === id);
      if (idx === -1) {
        return Result.notFound('Backoffice.Species.NotFound', `Species with id ${id} not found`);
      }
      dbSpecies.splice(idx, 1);
      return Result.ok(undefined);
    }))
    .intercept(new UpdateSpeciesName().handle(async (input, ctx) => {
      return saveSpecies(getSpecies(ctx)
        .maybe((value, meta) => {
          value.name = input.name;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateSpeciesDescription().handle(async (input, ctx) => {
      return saveSpecies(getSpecies(ctx)
        .maybe((value, meta) => {
          value.description = input.description;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateSpeciesThumbnail().handle(async (input, ctx) => {
      return saveSpecies(getSpecies(ctx)
        .maybe((value, meta) => {
          if (!input.image) {
            value.thumbnail = undefined;
          } else {
            value.thumbnail = { url: 'data:' + input.image.mimeType + ';base64,' + input.image.base64Content };
          }
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateSpeciesUtilizesCommands().handle(async (input, ctx) => {
      return saveSpecies(getSpecies(ctx)
        .maybe((value, meta) => {
          value.utilizesCommands = input.utilizesCommands;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateSpeciesUsesLitterBox().handle(async (input, ctx) => {
      return saveSpecies(getSpecies(ctx)
        .maybe((value, meta) => {
          value.usesLitterBox = input.usesLitterBox;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateSpeciesIsIndoorOutdoorPet().handle(async (input, ctx) => {
      return saveSpecies(getSpecies(ctx)
        .maybe((value, meta) => {
          value.isIndoorOutdoorPet = input.isIndoorOutdoorPet;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateSpeciesState().handle(async (input, ctx) => {
      return saveSpecies(getSpecies(ctx)
        .maybe((value, meta) => {
          value.state = transitionState(meta, value.state, input.stateTransition);
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new AddBreed().handle(async (input, ctx) => {
      return saveSpecies(getSpecies(ctx)
        .maybe((species, meta) => {
          const newBreed: Breed = {
            id: generateUUID(),
            speciesId: species.id,
            name: input.name,
            translationKey: appender('species', species.name + ' ' + input.name, input.name),
          };
          species.breeds.push({ breed: newBreed, sortId: species.breeds.length + 1 });
          return Result.ok(species, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new RemoveBreed().handle(async (input, ctx) => {
      const result = getBreed(ctx);
      if (!result.success) return result.convertToEmpty();
      const { species, breed } = result.value;
      const idx = species.breeds.findIndex(b => b.breed.id === breed.id);
      if (idx !== -1) {
        species.breeds.splice(idx, 1);
        saveSpecies(Result.ok(species), ctx);
      }
      return Result.ok(undefined);
    }))
    .intercept(new UpdateBreedName().handle(async (input, ctx) => {
      return saveSpecies(getBreed(ctx)
        .maybe(({ species, breed }, meta) => {
          breed.name = input.name;
          return Result.ok(species, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new MoveBreed().handle(async (input, ctx) => {
      return saveSpecies(getSpecies(ctx)
        .maybe((species, meta) => {
          species.breeds.sort((a, b) => a.sortId - b.sortId);
          const index = species.breeds.findIndex(b => b.breed.id === ctx.getPathParameter<string>('breedId'));
          if (index === -1) return Result.ok(species, meta);
          handleInPlaceMoveSortOfIdElement(species.breeds, index, input.moveInstruction.moveDirection);
          return Result.ok(species, meta);
        }), ctx)
        .convertToEmpty();
    }))
}